import classNames from "classnames";
import styles from "./LanguageSwitcher.module.scss";
import { FloatingIconButton, TextCard } from "@livingmap/hippo";
import { useEffect, useRef } from "react";
import { Language } from "@redux/services/types";

export interface Props {
  dataQA: string;
  className?: string;
  availableLanguages: Language[];
  currentLanguage: string;
  open: boolean;
  onButtonClick: () => void;
  onLanguageSelect: (lang: Language) => void;
  headerText: string;
}

const LanguageSwitcher: React.FC<Props> = ({
  dataQA,
  className,
  availableLanguages,
  currentLanguage,
  open,
  onButtonClick,
  onLanguageSelect,
  headerText,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onButtonClick(); // Close the panel when clicked outside
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onButtonClick]);

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, className)}
      ref={panelRef}
    >
      <FloatingIconButton
        dataQA="desktop-language-switcher"
        icon="language"
        size="regular"
        iconSize={24}
        type="primary"
        onClick={onButtonClick}
      />
      {open && (
        <div className={styles.optionsContainer}>
          <span className={styles.optionsHeader}>{headerText}</span>
          <ul className={styles.availableLanguages}>
            {availableLanguages.map((lang) => {
              return (
                <li key={lang.code}>
                  <TextCard
                    dataQA={`language-selection-${lang.code}`}
                    title={lang.human_readable_language}
                    subtitle={lang.human_readable_region}
                    onClick={() => onLanguageSelect(lang)}
                    active={lang.code === currentLanguage}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
